import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Award, Building2, ShieldCheck, Users, Linkedin, Mail } from 'lucide-react';
import Reveal from '../components/Reveal';
import CounterStat from '../components/CounterStat';
import { COMPANY, STATS, ACCREDITATIONS, SECTORS, TEAM, PARTNERS } from '../data/mock';

const PILLARS = [
  { icon: Building2, title: 'Specialist delivery', text: 'A studio built around two specialist disciplines — Interior fit-out and residential Construction — each led by a board-level director.' },
  { icon: Users, title: 'Built on people', text: 'Our values are firmly based on relationships with clients, colleagues and supply chain. Many of our team have built their careers with us.' },
  { icon: ShieldCheck, title: 'Honest delivery', text: 'Honesty, flexibility and innovation — Marvin Bennett’s founding values still sit at the centre of every Bespoke project.' },
  { icon: Award, title: '25+ years building', text: 'Over twenty-five years’ experience delivering interior fit-out, new build and conversion projects nationwide for clients including Amazon, Winvic, M&S and Lidl.' },
];

const TIMELINE = [
  { year: 1999, title: 'Studio founded', text: 'Marvin Bennett founds Bespoke Construction Services in Telford, Shropshire — with a focus on quality, honesty and flexibility.' },
  { year: 2005, title: 'First major distribution fit-out', text: 'Bespoke delivers its first national-grade distribution interior, beginning a long-standing relationship with Tier-1 contractors.' },
  { year: 2012, title: 'Construction division established', text: 'Residential new-build & conversion division formed, expanding the studio into Principal Contractor delivery.' },
  { year: 2018, title: 'FIS Certificate of Excellence', text: 'Recognised by the Finishes & Interiors Sector for high standards of workmanship and technical expertise.' },
  { year: 2021, title: 'Sapphire Court — best conversion', text: 'Investin PLC describe Sapphire Court as the best conversion project they have, to date, been involved in.' },
  { year: 2025, title: 'Boulevard, Bristol Street tops out', text: 'Major BtR scheme tops out in Birmingham as multiple national projects continue across the UK.' },
];

export default function About() {
  return (
    <div className="bg-[var(--bg)]">
      {/* Hero */}
      <section className="pt-36 pb-16 border-b border-[var(--line)]">
        <div className="container-rs">
          <span className="section-index"><span className="coral-dot" /> About</span>
          <Reveal>
            <h1 className="font-display text-7xl md:text-[150px] leading-[0.92] mt-6">A business built<br /><span className="font-display-italic text-[var(--coral)]">on people.</span></h1>
          </Reveal>
          <div className="grid md:grid-cols-12 gap-8 mt-10">
            <p className="md:col-span-7 md:col-start-6 text-[18px] md:text-[20px] text-[var(--ink-mute)] leading-relaxed">
              Founded in {COMPANY.established} by {COMPANY.founder}, Bespoke Construction Services is an award-winning UK construction company. Our values are firmly based on relationships with clients, colleagues and supply chain — with over twenty-five years’ experience delivering interior fit-out, new build and conversion projects nationwide.
            </p>
          </div>
        </div>
      </section>

      {/* Big image + tagline */}
      <section className="py-20">
        <div className="container-rs">
          <div className="relative overflow-hidden rounded-3xl">
            <img src="https://bespokeconstruction.co.uk/wp-content/uploads/2024/10/001_Bristol-St_Bristol-Development_Exterior-800x800.jpg" alt="Bristol Street" className="w-full h-[600px] md:h-[760px] object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[var(--navy)] via-[var(--navy)]/30 to-transparent" />
            <div className="absolute bottom-12 left-12 right-12 text-white">
              <div className="font-tag opacity-80">{COMPANY.tagline}</div>
              <div className="font-display text-4xl md:text-7xl mt-4 max-w-4xl leading-[0.98]">A 360 perspective in <span className="font-display-italic text-[var(--coral)]">a fast-moving environment.</span></div>
            </div>
          </div>
        </div>
      </section>

      {/* Pillars */}
      <section className="py-24">
        <div className="container-rs">
          <span className="section-index"><span className="coral-dot" /> Our Values</span>
          <h2 className="font-display text-5xl md:text-7xl mt-6 leading-[0.95]">Honest. Flexible. <span className="font-display-italic text-[var(--coral)]">Innovative.</span></h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mt-16">
            {PILLARS.map((p, i) => (
              <Reveal key={p.title} delay={i * 80}>
                <div className="soft-card p-8 h-full tilt">
                  <div className="w-12 h-12 rounded-full bg-[var(--coral)]/10 text-[var(--coral)] flex items-center justify-center"><p.icon size={20} /></div>
                  <div className="font-display text-3xl mt-6">{p.title}</div>
                  <p className="mt-3 text-[14px] text-[var(--ink-mute)] leading-relaxed">{p.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-24 bg-[var(--navy)] text-white">
        <div className="container-rs grid grid-cols-2 lg:grid-cols-4 gap-12">
          {STATS.map((s) => <CounterStat key={s.label} {...s} />)}
        </div>
      </section>

      {/* Timeline */}
      <section className="py-28 bg-[var(--bg-soft)]">
        <div className="container-rs">
          <span className="section-index"><span className="coral-dot" /> The story</span>
          <h2 className="font-display text-5xl md:text-7xl mt-6 leading-[0.95]">Twenty-five years, <span className="font-display-italic text-[var(--coral)]">honestly built.</span></h2>
          <div className="mt-16 grid md:grid-cols-12 gap-10">
            <div className="md:col-span-4 sticky top-32 self-start hidden md:block">
              <img src="https://bespokeconstruction.co.uk/wp-content/uploads/2025/07/IMG_3141-800x800.jpeg" alt="" className="w-full h-[480px] object-cover rounded-2xl" />
            </div>
            <div className="md:col-span-8">
              {TIMELINE.map((t, i) => (
                <Reveal key={t.year} delay={i * 70} className="grid grid-cols-[100px_1fr] gap-6 py-8 border-t border-[var(--line)]">
                  <div className="font-display text-3xl md:text-4xl text-[var(--coral)]">{t.year}</div>
                  <div>
                    <div className="font-display text-2xl md:text-3xl">{t.title}</div>
                    <p className="mt-3 text-[14px] text-[var(--ink-mute)] leading-relaxed max-w-2xl">{t.text}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Board */}
      <section className="py-24">
        <div className="container-rs">
          <div className="flex items-end justify-between gap-8 flex-wrap">
            <div>
              <span className="section-index"><span className="coral-dot" /> The Board</span>
              <h2 className="font-display text-5xl md:text-7xl mt-6 leading-[0.95]">Led by <span className="font-display-italic text-[var(--coral)]">five directors.</span></h2>
            </div>
            <Link to="/team" className="font-tag u-link">All team —</Link>
          </div>
          <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-6 mt-16">
            {TEAM.map((m, i) => (
              <Reveal key={m.id} delay={i * 80} className="group">
                <div className="relative overflow-hidden rounded-2xl aspect-[4/5]">
                  <img src={m.image} alt={m.name} className="absolute inset-0 w-full h-full object-cover zoom-img" />
                </div>
                <div className="mt-4">
                  <div className="font-tag text-[var(--coral)]">{m.role}</div>
                  <div className="font-display text-2xl mt-1">{m.name}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* End-users / clients */}
      <section className="py-24 bg-[var(--bg-soft)]">
        <div className="container-rs">
          <span className="section-index"><span className="coral-dot" /> End-Users</span>
          <h2 className="font-display text-5xl md:text-7xl mt-6 leading-[0.95]">Trusted by <span className="font-display-italic text-[var(--coral)]">national names.</span></h2>
          <p className="mt-6 max-w-2xl text-[var(--ink-mute)] leading-relaxed">Our portfolio of clients with end-users includes Amazon, Marks & Spencer, Lidl, Ocado, DPD, Jaguar Land Rover, Shropshire NHS Trust, First Industrial and Donnington Race Track.</p>
          <div className="mt-12 flex flex-wrap items-center gap-x-10 gap-y-5">
            {PARTNERS.map((p) => (
              <span key={p} className="font-display text-2xl md:text-4xl text-[var(--ink)]/55 hover:text-[var(--coral)] transition-colors cursor-default">{p}</span>
            ))}
          </div>
        </div>
      </section>

      {/* Sectors */}
      <section className="py-24">
        <div className="container-rs">
          <span className="section-index"><span className="coral-dot" /> Sectors</span>
          <h2 className="font-display text-5xl md:text-7xl mt-6 leading-[0.95]">Across <span className="font-display-italic text-[var(--coral)]">every</span> sector.</h2>
          <div className="mt-12 flex flex-wrap gap-3">
            {SECTORS.map((s) => (
              <span key={s} className="px-6 py-4 rounded-full border border-[var(--line)] hover:border-[var(--coral)] hover:text-[var(--coral)] transition-colors text-[14px] font-medium">{s}</span>
            ))}
          </div>
        </div>
      </section>

      {/* Accreditations */}
      <section className="py-24 bg-[var(--bg-soft)]">
        <div className="container-rs grid md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-6">
            <span className="section-index"><span className="coral-dot" /> Accreditations</span>
            <h2 className="font-display text-5xl md:text-7xl mt-6 leading-[0.95]">Independently <span className="font-display-italic text-[var(--coral)]">verified.</span></h2>
          </div>
          <p className="md:col-span-5 md:col-start-8 text-[var(--ink-mute)] leading-relaxed">NHBC, CHAS, Constructionline, FIS Accredited Member, FDIS Certificated Inspector, ICW Insured and METSEC Approved — all third-party audited and renewed annually.</p>
        </div>
        <div className="container-rs mt-12 flex flex-wrap gap-3">
          {ACCREDITATIONS.map((a) => (
            <span key={a} className="px-6 py-4 rounded-full border border-[var(--line)] bg-white text-[12px] tracking-[0.18em] uppercase font-semibold">{a}</span>
          ))}
        </div>
        <div className="container-rs mt-16">
          <Link to="/contact" className="pill pill-coral"><span>Start a Conversation</span><ArrowUpRight size={14} /></Link>
        </div>
      </section>
    </div>
  );
}
